import { fetcher } from '../../../lib/api-fetcher';
import { useMemo } from 'react';
import { createFileRoute, useNavigate, useSearch, Link } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Button } from '../../../components/ui/Button';
import { defineListPage, useListPage } from '@repo/list';
import type { PaginatedQueryFn } from '@repo/list';
import { DataTable, createUrlAdapter } from '@repo/web-list';
import { usePageTitle } from '../../../lib/hooks';
import type { CustomerListItem } from '../../../features/customers/types';
import { Plus } from 'lucide-react';


function maskMobile(mobile: string | null) {
  if (!mobile || mobile.length < 4) return mobile ?? '—';
  return `******${mobile.slice(-4)}`;
}

const customersConfig = defineListPage<CustomerListItem>({
  variant: 'data-table',
  resource: 'customers',
  searchable: true,
  columns: [
    { id: 'customerCode', header: 'Customer ID', accessor: 'customerCode', renderType: 'code' },
    { id: 'name', header: 'Name', accessor: 'name', sortable: true },
    { id: 'mobile', header: 'Mobile', accessor: 'mobile' },
  ],
  onRowClick: (row) => window.open(`/customers/${row.id}`, '_self'),
  defaultPageSize: 20,
});

const fetchCustomers: PaginatedQueryFn<CustomerListItem> = async (params) => {
  const qs = new URLSearchParams({ page: params.page || '1', limit: params.limit || '20' });
  if (params.search) qs.append('q', params.search);
  return fetcher.get<{ data: CustomerListItem[]; meta: { total: number; page: number; limit: number } }>(
    `/staff/v1/customers?${qs}`,
  );
};

export const Route = createFileRoute('/_auth/customers/')({
  component: CustomersIndexRoute,
});

function CustomersIndexRoute() {
  usePageTitle('Customers');
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const urlAdapter = useMemo(
    () => createUrlAdapter(search, navigate as never),
    [search, navigate],
  );

  const list = useListPage(customersConfig, fetchCustomers, urlAdapter);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Customers"
        subtitle="Registered subscribers and members across schemes."
        action={
          <Button asChild variant="primary">
            <Link to="/customers/new">
              <Plus className="h-4 w-4 mr-1.5" />
              New Customer
            </Link>
          </Button>
        }
      />

      <DataTable
        config={customersConfig}
        list={list}
        customRenderers={{
          mobile: (row) => (
            <span className="font-mono text-sm text-text-secondary">{maskMobile(row.mobile)}</span>
          ),
        }}
      />
    </div>
  );
}
